/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Link, PageGridContainer, breakpoints } from '@coursera/cds-core';

import type { ImageWithMetadataFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import useIsTablet from 'bundles/unified-common/hooks/useIsTablet';

const PARTNER_LOGO_MAX_HEIGHT = 25;
const PARTNER_LOGO_MAX_WIDTH = 80;
interface PartnerLogoHeights {
  [key: string]: number;
}

const CUSTOM_PARTNER_LOGO_HEIGHTS: PartnerLogoHeights = {
  duke: 20,
  umich: 35,
  ibm: 30,
};

const styles = {
  floatingBar: css`
    margin-top: calc(var(--cds-spacing-400) * -1);

    ${breakpoints.down('xs')} {
      margin-top: 0;
      padding-top: var(--cds-spacing-200);
    }
  `,
  container: css`
    height: 68px;
    width: 100%;
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-max);
    box-shadow: 0 0 40px -8px rgb(0 0 0 / 16%), 0 0 24px -16px rgb(0 0 0 / 16%);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: var(--cds-spacing-150);
    padding: 0 var(--cds-spacing-300);
    margin: 0 var(--cds-spacing-200);
  `,
  mobileContainer: css`
    text-align: center;
    width: 100%;
    margin: 0 auto;
  `,
  partnerLogo: css`
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;

    ${breakpoints.down('xs')} {
      flex: 0 25%;
      width: 82px;
      height: 52px;
    }
  `,
  mobileLogosContainer: css`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
  `,
};

type Props = {
  partnerLogos: ImageWithMetadataFieldsFragment[];
  isMobile?: boolean;
};

const PartnerLogosBar = ({ partnerLogos, isMobile }: Props) => {
  const isTablet = useIsTablet();

  let partnerLogoSizePercentage = isTablet ? '90%' : '100%';
  if (isMobile) {
    partnerLogoSizePercentage = '75%';
  }

  const getPartnerLogos = () => {
    return partnerLogos.map(({ relationSlug, image, imageAltText }) => {
      const url = image?.url;

      if (!url || !relationSlug) {
        return null;
      }

      return (
        <Link css={styles.partnerLogo} href={`/partners/${relationSlug}`} data-id={relationSlug} key={relationSlug}>
          <Imgix
            alt={imageAltText ?? ''}
            src={url}
            style={{ maxWidth: partnerLogoSizePercentage, maxHeight: partnerLogoSizePercentage }}
            maxWidth={PARTNER_LOGO_MAX_WIDTH}
            maxHeight={CUSTOM_PARTNER_LOGO_HEIGHTS[relationSlug] || PARTNER_LOGO_MAX_HEIGHT}
          />
        </Link>
      );
    });
  };

  return (
    <section css={styles.floatingBar}>
      <PageGridContainer>
        <div css={isMobile ? styles.mobileLogosContainer : styles.container}>{getPartnerLogos()}</div>
      </PageGridContainer>
    </section>
  );
};

export default PartnerLogosBar;
